import type { ChartHighlight, HighlightSentiment } from "./types";

// Sentiment colors for chart highlights (dark theme compatible)
export const SENTIMENT_COLORS = {
  positive: "rgba(34, 197, 94, 0.85)",   // green
  negative: "rgba(239, 68, 68, 0.85)",   // red
  neutral: "rgba(148, 163, 184, 0.85)",  // slate
} as const;

// Subtle background colors for range highlights
export const SENTIMENT_BG_COLORS = {
  positive: "rgba(34, 197, 94, 0.08)",
  negative: "rgba(239, 68, 68, 0.08)",
  neutral: "rgba(148, 163, 184, 0.06)",
} as const;

/**
 * Get the highlight color for a given sentiment.
 */
export function sentimentColor(sentiment: HighlightSentiment): string {
  return SENTIMENT_COLORS[sentiment] ?? SENTIMENT_COLORS.neutral;
}

/**
 * Get the subtle background color for range highlights.
 */
export function sentimentBgColor(sentiment: HighlightSentiment): string {
  return SENTIMENT_BG_COLORS[sentiment] ?? SENTIMENT_BG_COLORS.neutral;
}

/**
 * Find a matching point highlight for a given x-axis value and optional y_key.
 * Returns the first matching highlight, or undefined if none found.
 *
 * Matches against raw row values (not formatted axis labels).
 */
export function findHighlight(
  highlights: ChartHighlight[] | undefined,
  indexValue: string | number | null | undefined,
  yKey?: string,
): ChartHighlight | undefined {
  if (!highlights?.length || indexValue == null) return undefined;

  const indexStr = String(indexValue);

  return highlights.find((h) => {
    if (h.type !== "point") return false;
    if (h.index_value !== indexStr) return false;
    // y_key null means "all series"
    if (h.y_key != null && h.y_key !== yKey) return false;
    return true;
  });
}

/**
 * Get all range highlights, optionally filtered by y_key.
 */
export function getRangeHighlights(
  highlights: ChartHighlight[] | undefined,
  yKey?: string,
): ChartHighlight[] {
  if (!highlights?.length) return [];

  return highlights.filter((h) => {
    if (h.type !== "range") return false;
    if (h.y_key != null && h.y_key !== yKey) return false;
    return true;
  });
}

/**
 * Create a vertical linear gradient for bar charts (dark at top → lighter at bottom).
 * Works with both hex colors and rgba strings.
 */
export function barGradient(baseColor: string, horizontal = false) {
  return {
    type: "linear" as const,
    x: 0, y: 0, x2: horizontal ? 1 : 0, y2: horizontal ? 0 : 1,
    colorStops: horizontal
      ? [
          { offset: 0, color: baseColor },
          { offset: 1, color: adjustOpacity(baseColor, 0.55) },
        ]
      : [
          { offset: 0, color: adjustOpacity(baseColor, 0.55) },
          { offset: 1, color: baseColor },
        ],
  };
}

/**
 * Adjust the opacity of a color string.
 * Supports hex (#rrggbb) and rgba(r, g, b, a) formats.
 */
function adjustOpacity(color: string, opacity: number): string {
  // Handle rgba
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbaMatch) {
    return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${opacity})`;
  }
  // Handle hex
  const hex = color.replace("#", "");
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return color;
}

/**
 * Chart types that support highlight rendering.
 *
 * `area` is a line chart with a fill, and the registry's underscore spellings
 * are the same charts as the hyphenated ones — a tile whose type is missing
 * here silently loses both its highlights and the toggle that turns them off.
 * `pie` and `donut` stay out: a highlight is a statement about one point on an
 * axis, and a disc has none. `bar_stacked_metrics` and `bar_stacked_metrics_100`
 * stay out too (plan 2026-10-17 stacked metrics, D9): a point highlight
 * carries no `y_key`, so it recolours EVERY segment of the column in one
 * sentiment colour, and the parts — the whole picture — disappear.
 */
const HIGHLIGHT_SUPPORTED_TYPES = new Set([
  "bar",
  "line",
  "area",
  "grouped-bar",
  "grouped_bar",
  "stacked-bar",
  "stacked_bar",
  "horizontal_bar",
  "timeseries",
  "combo",
  "category_comparison",
  // The line variants (plan chart library, S4). A highlight is a statement
  // about one point on an axis, and a smoothed line or a stacked area has the
  // same axis and the same points as the `line` and `area` already listed —
  // so leaving them out would have cost a reader the Insights switch for
  // picking a curve. Pinned by `chartStyleHighlights.test.ts` next door, which
  // reads CHART_STYLES rather than this list, so the next chart type cannot
  // arrive here silently.
  "line_smoothed",
  "area_stacked",
  // Group 3 (plan chart library, S7). Both draw one bar per value of a
  // CATEGORY axis, which is exactly the position a highlight names — a
  // waterfall's step and a 100% stack's column are as pointable as a plain
  // bar's. `scatter`, `bubble` and `treemap` are deliberately NOT here: see
  // the exclusion set in `chartStyleHighlights.test.ts` for why having axes
  // is not the same as having a position an insight can name.
  "waterfall",
  "bar_stacked_100",
  // A bridge's axis is a sequence of named positions like a waterfall's, so a
  // highlight has something to point AT. Worth stating what it will point at,
  // because two of those positions are not members: the opening and closing
  // bars are period totals, and "Other" is a remainder. An insight naming one
  // of those names a bar that exists, which is the bar the reader is looking
  // at - not a dimension value they can drill into.
  "bridge",
]);

/**
 * Check if a chart type supports highlight rendering.
 */
export function supportsHighlights(chartType: string | undefined): boolean {
  return !!chartType && HIGHLIGHT_SUPPORTED_TYPES.has(chartType);
}
